import { Player } from '@lottiefiles/react-lottie-player'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from '@mui/material'
import { applyActionCode, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth'
import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { object, string } from 'yup'

import SubmitButton from '../components/forms/SubmitButtonWrapper'
import Textfield from '../components/forms/TextfieldWrapper'
import LanguageSelect from '../components/LanguageSelect'
import Logo from '../dummy/Uride.png'
import { auth } from '../firebase/config'
import SuccessLottie from '../lottie/success-send-to-email.json'
import routes from '../router/routes'

type ActionState =
  | { email: string; status: 'reset_form' }
  | { message: string; status: 'reset_error' }
  | { message: string; status: 'verify_error' }
  | { status: 'invalid_mode' }
  | { status: 'loading' }
  | { status: 'reset_success' }
  | { status: 'verify_success' }

const resetPasswordSchema = object({
  password: string()
    .min(8, 'Must be at least 8 characters')
    .matches(
      /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/,
      'Must contain at least 1 special character.'
    )
    .required('Required'),
})

const IS_DEV = import.meta.env.VITE_ENVIRONMENT_NAME === 'development'

const DEV_STATES: Record<string, ActionState> = {
  invalid: { status: 'invalid_mode' },
  reset_error: { message: 'This link has expired (test).', status: 'reset_error' },
  reset_form: { email: 'driver@example.com', status: 'reset_form' },
  reset_success: { status: 'reset_success' },
  verify_error: { message: 'This link has expired (test).', status: 'verify_error' },
  verify_success: { status: 'verify_success' },
}

const EmailAction = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [state, setState] = useState<ActionState>({ status: 'loading' })

  const mode = searchParams.get('mode')
  const oobCode = searchParams.get('oobCode')
  const testState = IS_DEV ? searchParams.get('testState') : null

  useEffect(() => {
    if (testState && DEV_STATES[testState]) {
      setState(DEV_STATES[testState])
      return
    }

    if (!oobCode) {
      setState({ status: 'invalid_mode' })
      return
    }

    if (mode === 'verifyEmail') {
      applyActionCode(auth, oobCode)
        .then(() => setState({ status: 'verify_success' }))
        .catch((err: { code?: string }) =>
          setState({
            message:
              err.code === 'auth/invalid-action-code'
                ? t('auth.emailAction.errors.expired_link')
                : t('auth.emailAction.errors.generic'),
            status: 'verify_error',
          })
        )
    } else if (mode === 'resetPassword') {
      verifyPasswordResetCode(auth, oobCode)
        .then((email) => setState({ email, status: 'reset_form' }))
        .catch(() =>
          setState({
            message: t('auth.emailAction.errors.expired_link'),
            status: 'reset_error',
          })
        )
    } else {
      setState({ status: 'invalid_mode' })
    }
  }, [mode, oobCode, t, testState])

  const handlePasswordReset = async (values: { password: string }) => {
    if (oobCode) {
      try {
        await confirmPasswordReset(auth, oobCode, values.password)
        setState({ status: 'reset_success' })
      } catch {
        setState({
          message: t('auth.emailAction.errors.reset_failed'),
          status: 'reset_error',
        })
      }
    }
  }

  const getCardTitle = () => {
    if (mode === 'verifyEmail' || state.status.startsWith('verify'))
      return t('auth.emailAction.verify.title')
    if (mode === 'resetPassword' || state.status.startsWith('reset'))
      return t('auth.emailAction.reset.title')
    return t('auth.emailAction.invalid_title')
  }

  const renderContent = () => {
    switch (state.status) {
      case 'loading':
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )

      case 'verify_success':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Player
                autoplay
                keepLastFrame
                src={SuccessLottie}
                style={{ height: '160px' }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography textAlign="center" variant="h4">
                {t('auth.emailAction.verify.success_title')}
              </Typography>
              <Typography
                color="text.secondary"
                sx={{ mt: 1 }}
                textAlign="center"
                variant="body2"
              >
                {t('auth.emailAction.verify.success_description')}
              </Typography>
            </Grid>
            {/* Commenting to avoid app users going directly to portal */}
            {/* <Grid item xs={12}>
              <Button
                component={Link}
                disableElevation
                fullWidth
                size="large"
                to={routes.auth}
                variant="contained"
              >
                {t('auth.emailAction.verify.cta')}
              </Button>
            </Grid> */}
          </Grid>
        )

      case 'verify_error':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <ErrorOutlineIcon color="error" sx={{ fontSize: 80 }} />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography textAlign="center" variant="h4">
                {t('auth.emailAction.verify.error_title')}
              </Typography>
              <Typography
                color="text.secondary"
                sx={{ mt: 1 }}
                textAlign="center"
                variant="body2"
              >
                {state.message}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Button
                component={Link}
                fullWidth
                size="large"
                to={routes.auth}
                variant="text"
              >
                {t('auth.emailAction.back_to_signin')}
              </Button>
            </Grid>
          </Grid>
        )

      case 'reset_form':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography
                color="text.secondary"
                textAlign="center"
                variant="body2"
              >
                {t('auth.emailAction.reset.form_description', {
                  email: state.email,
                })}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Formik
                initialValues={{ password: '' }}
                onSubmit={handlePasswordReset}
                validationSchema={resetPasswordSchema}
              >
                {({ isSubmitting }) => (
                  <Form
                    style={{ display: 'flex', flexDirection: 'column', rowGap: 16 }}
                  >
                    <Textfield
                      fullWidth
                      label={t('auth.emailAction.reset.password_label')}
                      name="password"
                      type="password"
                    />
                    <SubmitButton
                      disabled={isSubmitting}
                      fullWidth
                      loading={isSubmitting}
                    >
                      {t('auth.emailAction.reset.cta')}
                    </SubmitButton>
                  </Form>
                )}
              </Formik>
            </Grid>
          </Grid>
        )

      case 'reset_success':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Player
                autoplay
                keepLastFrame
                src={SuccessLottie}
                style={{ height: '160px' }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography textAlign="center" variant="h4">
                {t('auth.emailAction.reset.success_title')}
              </Typography>
              <Typography
                color="text.secondary"
                sx={{ mt: 1 }}
                textAlign="center"
                variant="body2"
              >
                {t('auth.emailAction.reset.success_description')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Button
                component={Link}
                disableElevation
                fullWidth
                size="large"
                to={routes.auth}
                variant="contained"
              >
                {t('auth.emailAction.back_to_signin')}
              </Button>
            </Grid>
          </Grid>
        )

      case 'reset_error':
      case 'invalid_mode':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <ErrorOutlineIcon color="error" sx={{ fontSize: 80 }} />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography textAlign="center" variant="h4">
                {state.status === 'reset_error'
                  ? t('auth.emailAction.reset.error_title')
                  : t('auth.emailAction.invalid_title')}
              </Typography>
              <Typography
                color="text.secondary"
                sx={{ mt: 1 }}
                textAlign="center"
                variant="body2"
              >
                {state.status === 'reset_error'
                  ? state.message
                  : t('auth.emailAction.errors.invalid_link')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Button
                component={Link}
                fullWidth
                size="large"
                to={routes.auth}
                variant="text"
              >
                {t('auth.emailAction.back_to_signin')}
              </Button>
            </Grid>
          </Grid>
        )

      default:
        return null
    }
  }

  return (
    <Container
      maxWidth={false}
      sx={{
        backgroundColor: '#FAF9F9',
        minHeight: '100vh',
        p: 2,
      }}
    >
      <Grid
        alignContent="flex-start"
        alignItems="center"
        container
        justifyContent="center"
        spacing={2}
      >
        <Grid
          item
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            mt: 4,
            rowGap: 1,
          }}
          xs={12}
        >
          <img alt="Uride Logo" src={Logo} width="150px" />
          <Typography color="text.primary" variant="body1">
            {t('auth.tagline')}
          </Typography>
        </Grid>
        <Grid
          item
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
          }}
          xs={12}
        >
          <LanguageSelect />
        </Grid>
        <Grid item lg={4} xs={12}>
          <Card>
            <CardHeader
              title={
                <Typography align="center" color="text.primary" variant="h4">
                  {getCardTitle()}
                </Typography>
              }
            />
            <CardContent>{renderContent()}</CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  )
}

export default EmailAction
